import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { ArrowLeft, ExternalLink, ShieldCheck, Eye } from "lucide-react";

const DEMO_SLUG = "acme-store";
const DEMO_SHOP = {
    name: "ACME Store",
    company_name: "ACME Handel GmbH",
    address_line1: "Musterstraße 12",
    postal_code: "10115",
    city: "Berlin",
    country: "Deutschland",
    contact_email: "service@acme-store.example",
    url: "https://acme-store.example",
    brand_color: "#0b905d",
    language: "de",
    logo_url: "",
};

export default function Demo() {
    const { t } = useI18n();
    const brand = DEMO_SHOP.brand_color;

    return (
        <div className="min-h-screen bg-white" data-testid="demo-page">
            {/* Nav */}
            <header className="border-b border-gray-200 bg-white/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                            <span className="text-white font-display font-bold">W</span>
                        </div>
                        <span className="font-display font-bold text-lg tracking-tight text-[#00153f]">
                            widerruf<span className="text-[#0b905d] text-sm font-semibold">.online</span>
                        </span>
                    </Link>
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f]" data-testid="demo-back">
                        <ArrowLeft className="w-4 h-4" /> {t("back")}
                    </Link>
                </div>
            </header>

            {/* Intro */}
            <section className="bg-grain">
                <div className="max-w-5xl mx-auto px-6 pt-14 pb-10 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-[#0b905d] text-xs font-mono uppercase tracking-widest mb-5">
                        <Eye className="w-3.5 h-3.5" /> {t("demo_eyebrow")}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#00153f]">{t("demo_title")}</h1>
                    <p className="mt-3 text-gray-600 max-w-2xl mx-auto">{t("demo_sub")}</p>

                    <div className="mt-8 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-2.5 font-mono text-sm">
                        <span className="text-gray-400">URL:</span>
                        <span className="text-[#00153f]">/w/{DEMO_SLUG}</span>
                        <Link to={`/w/${DEMO_SLUG}`} target="_blank">
                            <Button size="sm" variant="outline" className="ml-2" data-testid="demo-open-new-tab">
                                <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Browser frame mockup */}
            <section className="pb-20">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="rounded-lg border border-gray-200 shadow-md overflow-hidden bg-white" data-testid="demo-frame">
                        {/* Browser bar */}
                        <div className="bg-slate-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-red-300" />
                            <span className="w-3 h-3 rounded-full bg-amber-300" />
                            <span className="w-3 h-3 rounded-full bg-emerald-300" />
                            <div className="ml-3 flex-1 bg-white border border-gray-200 rounded-md px-3 py-1 text-xs text-gray-500 font-mono">
                                widerruf.online/w/{DEMO_SLUG}
                            </div>
                        </div>

                        {/* Demo content (shop header + form preview) */}
                        <div>
                            <div style={{ background: brand }} className="text-white">
                                <div className="max-w-3xl mx-auto px-6 py-8 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-md bg-white/15 flex items-center justify-center font-display font-bold">A</div>
                                    <div>
                                        <div className="font-display font-bold text-lg leading-tight">{DEMO_SHOP.name}</div>
                                        <div className="text-xs text-white/70">{DEMO_SHOP.url}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="max-w-3xl mx-auto px-6 py-10 bg-slate-50">
                                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">
                                    <ShieldCheck className="w-3.5 h-3.5" /> EU 2023/2673
                                </div>
                                <h2 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{t("public_title")}</h2>
                                <p className="text-gray-600 mt-2">{t("public_sub")}</p>

                                {/* Static form preview */}
                                <div className="mt-8 bg-white border border-gray-200 rounded-md p-6 space-y-4">
                                    <PreviewField label={t("public_full_name")} value="Maria Becker" />
                                    <PreviewField label={t("public_email")} value="maria.becker@example.com" />
                                    <PreviewField label={t("public_address")} value={t("demo_ex_address")} />
                                    <div className="grid grid-cols-2 gap-3">
                                        <PreviewField label={t("public_zip")} value="80331" />
                                        <PreviewField label={t("public_city")} value={t("demo_ex_city")} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <PreviewField label={t("public_order_number")} value="ORD-2026-00482" />
                                        <PreviewField label={t("public_order_date")} value="2026-05-14" />
                                    </div>
                                    <PreviewField label={t("public_products")} value={t("demo_ex_products")} multi />
                                    <PreviewField label={t("public_reason")} value={t("demo_ex_reason")} multi />
                                </div>

                                <div className="mt-5 text-center">
                                    <Link to={`/w/${DEMO_SLUG}`} target="_blank">
                                        <Button className="h-11 px-6 text-white" style={{ background: brand }} data-testid="demo-try-live">
                                            {t("demo_try_live")} <ExternalLink className="ml-2 w-4 h-4" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="mt-6 text-xs text-gray-500 text-center">{t("demo_footnote")}</p>
                </div>
            </section>
        </div>
    );
}

function PreviewField({ label, value, multi }) {
    return (
        <div>
            <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-1.5">{label}</div>
            <div className={`border border-gray-200 rounded-md px-3 py-2 text-sm text-[#00153f] bg-slate-50 ${multi ? "min-h-[60px]" : ""}`}>{value}</div>
        </div>
    );
}
